/* ============================================================================
 * Payroll & Fixed Expenses — the client for /api/net-profit.
 *
 * Every figure the Analyzer and the Sales Report's Fixed Expense tab show comes from the
 * server ledger through these calls; the screens never do their own arithmetic on money
 * (brief §3 rule 8). A refusal comes back as an NpError carrying the server's `code` and any
 * extra it sent (an overpayment's extra amount, the arrears a back-dated raise suggests), so a
 * sheet can ask the right follow-up question instead of showing a bare error.
 * ========================================================================== */
import http from '../httpClient';

export class NpError extends Error {
  constructor(message, { code = 'ERROR', status = 0, data = null } = {}) {
    super(message);
    this.code = code;
    this.status = status;
    this.data = data || {};
  }
}

const OFFLINE = 'No connection — nothing was saved. Your entries are still here; try again.';

async function call(method, path, body) {
  let res;
  try {
    if (method === 'get') res = await http.get(`/net-profit${path}`);
    else if (method === 'delete') res = await http.delete(`/net-profit${path}`, body || {});
    else res = await http[method](`/net-profit${path}`, body || {});
  } catch (err) {
    const data = err && err.data && typeof err.data === 'object' ? err.data : {};
    if (!err || (!err.status && /fetch|network/i.test(String(err.message || '')))) {
      throw new NpError(OFFLINE, { code: 'OFFLINE' });
    }
    throw new NpError(data.error || err.message || 'Something went wrong', { code: data.code || 'ERROR', status: err.status || 0, data });
  }
  if (res && res.success === false) {
    throw new NpError(res.error || 'You do not have permission to do this', { code: res.code || 'FORBIDDEN', status: 403, data: res });
  }
  return res || {};
}

const q = (params) => {
  const s = Object.entries(params || {})
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return s ? `?${s}` : '';
};

const npApi = {
  // reads
  status: () => call('get', '/status'),
  month: (key) => call('get', `/month/${key}`),
  trend: (n = 6) => call('get', `/trend${q({ n })}`),
  staff: (month) => call('get', `/staff${q({ month })}`),
  worker: (id, month) => call('get', `/workers/${id}${q({ month })}`),
  item: (id, month) => call('get', `/items/${id}${q({ month })}`),
  audit: (params) => call('get', `/audit${q(params)}`),
  nameCheck: (name) => call('get', `/name-check${q({ name })}`),
  outlets: () => call('get', '/outlets'),
  users: () => call('get', '/users'),
  fixedShare: (queryString) => call('get', `/fixed-share${queryString || ''}`),

  // workers
  createWorker: (p) => call('post', '/workers', p),
  updateWorker: (id, p) => call('patch', `/workers/${id}`, p),
  changeSalary: (id, p) => call('post', `/workers/${id}/rates`, p),
  cancelRate: (rateId) => call('post', `/rates/${rateId}/cancel`),
  giveAdvance: (id, p) => call('post', `/workers/${id}/advance`, p),
  recoverAdvance: (id, p) => call('post', `/workers/${id}/recover`, p),
  paySalary: (id, p) => call('post', `/workers/${id}/pay`, p),
  archiveWorker: (id, p) => call('post', `/workers/${id}/archive`, p),
  settleAdvance: (id, p) => call('post', `/workers/${id}/settle`, p),
  restoreWorker: (id, p) => call('post', `/workers/${id}/restore`, p),
  deleteWorker: (id) => call('delete', `/workers/${id}`),

  // a month's entries
  setInputs: (entryId, p) => call('post', `/entries/${entryId}/inputs`, p),
  addAdjustment: (entryId, p) => call('post', `/entries/${entryId}/adjustments`, p),
  removeAdjustment: (adjId) => call('delete', `/adjustments/${adjId}`),
  confirmEntry: (entryId, p) => call('post', `/entries/${entryId}/confirm`, p),
  payLater: (entryId) => call('post', `/entries/${entryId}/pay-later`),
  noSalary: (entryId, p) => call('post', `/entries/${entryId}/no-salary`, p),
  unconfirm: (entryId) => call('post', `/entries/${entryId}/unconfirm`),
  confirmBill: (entryId, p) => call('post', `/entries/${entryId}/bill`, p),
  notReceived: (entryId) => call('post', `/entries/${entryId}/not-received`),
  confirmAllSalaries: (key) => call('post', `/month/${key}/confirm-salaries`),
  confirmAllDrafts: (key) => call('post', `/month/${key}/confirm-drafts`),
  closeMonth: (key, p) => call('post', `/month/${key}/close`, p),
  reopenMonth: (key, p) => call('post', `/month/${key}/reopen`, p),

  // expenses
  createItem: (p) => call('post', '/items', p),
  updateItem: (id, p) => call('patch', `/items/${id}`, p),
  changeItemAmount: (id, p) => call('post', `/items/${id}/amount`, p),
  lateAdjustment: (id, p) => call('post', `/items/${id}/late`, p),
  payExpense: (id, p) => call('post', `/items/${id}/pay`, p),
  stopItem: (id, p) => call('post', `/items/${id}/stop`, p),
  removeSpreadEarly: (id, p) => call('post', `/items/${id}/remove-early`, p),
  deleteItem: (id) => call('delete', `/items/${id}`),

  // payments, settings, import
  editPayment: (id, p) => call('patch', `/payments/${id}`, p),
  deletePayment: (id, p) => call('delete', `/payments/${id}`, p),
  updateSettings: (p) => call('patch', '/settings', p),
  importLocal: (items) => call('post', '/import-local', { items }),
};

export default npApi;

/* ── formatting shared by the Analyzer and the Sales Report tab ─────────────── */
export const R = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
export const R2 = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthLong = (key) => { if (!key) return ''; const [y, m] = key.split('-').map(Number); return `${MONTHS_LONG[m - 1]} ${y}`; };
export const monthName = (key) => { if (!key) return ''; return MONTHS_LONG[Number(key.split('-')[1]) - 1]; };
export const monthShort = (key) => { if (!key) return ''; const [y, m] = key.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };
export const dayLabel = (d) => { if (!d) return '—'; const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number); return `${dd} ${MONTHS[m - 1]} ${y}`; };
export const dayShort = (d) => { if (!d) return '—'; const [, m, dd] = String(d).slice(0, 10).split('-').map(Number); return `${dd} ${MONTHS[m - 1]}`; };
export const addMonths = (key, n) => {
  const [y, m] = key.split('-').map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
};
export const istToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

/**
 * The profit chain (brief §2): net revenue → gross profit → operating profit → net profit.
 * Revenue and gross profit are read from the same /reports/summary figures the Sales Report's
 * cards print (its Gross Profit card reads this very field), so gross profit agrees on both
 * screens; expenses are the ledger's. The server's close snapshot uses the identical formula.
 */
export function profitChain(summary, totals) {
  const s = summary || {};
  const revenue = Number(s.total_sales) || 0;
  const pick = s.display_net_profit ?? s.net_profit ?? s.display_gross_profit ?? s.gross_profit;
  const grossProfit = Number(pick) || 0;
  const operating = Number(totals && totals.operating) || 0;
  const taxInterest = Number(totals && totals.taxInterest) || 0;
  const operatingProfit = grossProfit - operating;
  return {
    revenue,
    cogs: revenue - grossProfit,
    grossProfit,
    operatingExpenses: operating,
    operatingProfit,
    taxInterest,
    netProfit: operatingProfit - taxInterest,
    orders: Number(s.total_orders) || 0,
    cogsEstimated: !!(s.has_estimated_items || s.all_cogs_unavailable || Number(s.excluded_cogs_item_count || 0) > 0 || s.unmapped_cogs_estimated),
  };
}

/** The Sales Report summary for a whole month — the same request the report makes for a picked month. */
export async function monthSummary(key) {
  const businessId = Number(localStorage.getItem('businessId')) || (() => {
    try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return Number(u.businessId || u.business_id) || ''; } catch { return ''; }
  })();
  return http.get(`/reports/summary${q({ businessId, period: 'month', monthYear: key })}`);
}
